// modernCursorEditorPanel — Modern の「カーソル」フロート位置 pure helper.
//
// custom-cursor-ux フォローアップ (2026-09-19、作者 staging フィードバック §B):
// 「Modern のエディタをドラッグで動かしたい (Classic のフローティングと同じように)」。
//
// 統合案 (`panelRegistry` / `PanelId` / `FloatShell` への完全統合) はまず検討した:
// `PanelId` union 拡張は `ALL_PANEL_IDS` / `DEFAULT_WORKSPACE` (ホーム region) /
// `ModernLayout` の `PANEL_ARIA` / パネル表示メニューの label 一覧 / `panelRegistry` /
// `floatWidthFor` / `GROW_PANELS` / `ModernPhoneShell` の switch 分岐 / 複数 workspace
// テストへ波及する上、**意味論的にも噛み合わない**: `WorkspaceState` の
// docked/floating/hidden モデルは「常に存在し、表示/非表示・位置をユーザーが
// カスタマイズして永続化する」パネル用であり、カーソルエディタは「押した時だけ開く
// 一時的な作業窓」(= Classic 側 `cursorEditorPopup.ts` 冒頭コメントと同じ ephemeral
// 前提)。既存ユーザーの保存済みカスタム workspace へ「常設パネル」として注入する
// migration (`repairMissingPanels`) が走ってしまうのも本質的に不要な副作用。
// → 完全統合は見送り、**Classic `CursorEditorPopup`/`cursorEditorPopup.ts` と同型の
// 軽量フロート**にする (= 独自のドラッグ実装を新たに書かず、共有 geometry
// `shared/floatShellGeometry.ts` + `shared/usePanelDragGrip.ts` を使う)。

import type { StorageLike } from '../shared/customCursorHint.js';
import type { FloatPoint } from '../shared/floatShellGeometry.js';

/** popup のデフォルト位置を出す際の画面端マージン (px、Classic の CursorEditorPopup と揃える). */
export const MODERN_CURSOR_EDITOR_PANEL_MARGIN = 16;

/** panel のおおよその幅 / 高さ (= デフォルト位置 / 初回 clamp fallback 用). 旧固定 `w-[280px]` を踏襲. */
export const MODERN_CURSOR_EDITOR_PANEL_NOMINAL_WIDTH = 280;
export const MODERN_CURSOR_EDITOR_PANEL_NOMINAL_HEIGHT = 380;

/**
 * panel のデフォルト位置。旧実装の固定 `top-16 right-4` (= 画面右上寄り) と同じ見た目に
 * なるよう、画面右上を起点に計算する (= 初回表示の見た目を変えない)。
 */
export const defaultModernCursorEditorPanelPosition = (
  innerWidth: number,
  innerHeight: number,
): FloatPoint => {
  if (!Number.isFinite(innerWidth) || !Number.isFinite(innerHeight)) {
    return { x: MODERN_CURSOR_EDITOR_PANEL_MARGIN, y: MODERN_CURSOR_EDITOR_PANEL_MARGIN };
  }
  const x = Math.max(
    MODERN_CURSOR_EDITOR_PANEL_MARGIN,
    innerWidth - MODERN_CURSOR_EDITOR_PANEL_NOMINAL_WIDTH - MODERN_CURSOR_EDITOR_PANEL_MARGIN,
  );
  // 縦方向の可動域上限 (= panel がビューポートより高ければ上端マージンまで丸める、
  // `clampFloatPosition` と同じ「はみ出しよりは画面内優先」の考え方).
  const yMax = Math.max(
    MODERN_CURSOR_EDITOR_PANEL_MARGIN,
    innerHeight - MODERN_CURSOR_EDITOR_PANEL_NOMINAL_HEIGHT - MODERN_CURSOR_EDITOR_PANEL_MARGIN,
  );
  // 旧 `top-16` (= 4rem = 64px) を踏襲しつつ、上記可動域を超えない.
  const y = Math.min(64, yMax);
  return { x, y };
};

/** localStorage キー: panel 位置 (per-user 記憶, Classic CursorEditorPopup と同流儀). */
export const MODERN_CURSOR_EDITOR_PANEL_POS_KEY = 'oekaki:modern-cursor-editor-panel-pos';

/**
 * 保存済 position を読み出す. 不在 / 不正 JSON / storage 不可は `null`
 * (= caller がデフォルト位置に倒す). 例外は throw しない.
 */
export const readModernCursorEditorPanelPosition = (storage: StorageLike): FloatPoint | null => {
  try {
    const raw = storage.getItem(MODERN_CURSOR_EDITOR_PANEL_POS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { x?: unknown }).x === 'number' &&
      typeof (parsed as { y?: unknown }).y === 'number'
    ) {
      const { x, y } = parsed as { x: number; y: number };
      if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
    }
    return null;
  } catch {
    return null;
  }
};

/** position を保存. storage 不可 / quota 超過は silently drop. */
export const writeModernCursorEditorPanelPosition = (
  storage: StorageLike,
  pos: FloatPoint,
): void => {
  try {
    storage.setItem(MODERN_CURSOR_EDITOR_PANEL_POS_KEY, JSON.stringify({ x: pos.x, y: pos.y }));
  } catch {
    // 永続化できないだけで動作は継続させる.
  }
};
